import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Card, Button, Tooltip } from 'antd';
import { MessageSquare, CheckCircle } from 'lucide-react';
import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';

interface InputNodeData {
  label: string;
  placeholder?: string;
  description?: string;
  isExecuting?: boolean;
  isCompleted?: boolean;
  onDelete?: () => void;
  isUnlinked?: boolean;
}

const InputNode: React.FC<NodeProps<InputNodeData>> = ({ data, selected }) => {
  const { label, placeholder, description, isExecuting, isCompleted, onDelete, isUnlinked } = data;

  const getCardStyle = () => {
    if (isExecuting) {
      return {
        borderColor: '#1890ff',
        boxShadow: '0 0 10px rgba(24, 144, 255, 0.5)',
        animation: 'pulse 1.5s ease-in-out infinite'
      };
    }
    if (isCompleted) {
      return {
        borderColor: '#1890ff',
        backgroundColor: '#e6f7ff'
      };
    }
    if (selected) {
      return {
        borderColor: '#722ed1',
        boxShadow: '0 0 0 2px rgba(114, 46, 209, 0.2)'
      };
    }
    return {
      borderColor: '#1890ff'
    };
  };

  return (
    <>
      <Card
        size="small"
        style={{
          width: 180,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          position: 'relative',
          ...getCardStyle()
        }}
        className="input-node"
      >
        {/* Caution Icon for Unlinked Nodes */}
        {isUnlinked && (
          <Tooltip title="This node is not connected to any other nodes">
            <WarningOutlined
              style={{
                position: 'absolute',
                top: '4px',
                left: '4px',
                color: '#faad14',
                fontSize: '14px',
                zIndex: 10,
                backgroundColor: 'white',
                borderRadius: '50%',
                padding: '2px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
            />
          </Tooltip>
        )}
        
        {/* Delete Button */}
        {onDelete && (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              width: '20px',
              height: '20px',
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              opacity: 0.7,
              transition: 'opacity 0.2s'
            }}
            className="node-delete-btn"
          />
        )}
        
        <div className="flex items-center space-x-2">
          <div className="flex-shrink-0">
            <div className={`p-2 rounded-full ${isExecuting || isCompleted ? 'bg-blue-100' : 'bg-blue-100'}`}>
              {isCompleted ? (
                <CheckCircle className="h-4 w-4 text-blue-600" />
              ) : (
                <MessageSquare className="h-4 w-4 text-blue-600" />
              )}
            </div>
          </div>
          
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-900">
              {label}
            </h4>
            
            {description && (
              <p className="text-xs text-gray-500 mt-1">
                {description}
              </p>
            )}
            
            {placeholder && (
              <p className="text-xs text-gray-400 mt-1 italic">
                "{placeholder}"
              </p>
            )}
            
            {isExecuting && (
              <div className="mt-1">
                <div className="flex items-center space-x-1">
                  <div className="animate-spin h-2 w-2 border border-blue-600 border-t-transparent rounded-full" />
                  <span className="text-xs text-blue-600">Reading input...</span>
                </div>
              </div>
            )}
            
            {isCompleted && (
              <div className="mt-1">
                <span className="text-xs text-blue-600">Input captured</span>
              </div>
            )}
          </div>
        </div>
      </Card>
      
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: '#1890ff' }}
      />
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        
        .input-node:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }

        .input-node .node-delete-btn {
          opacity: 0;
          transition: opacity 0.2s;
        }

        .input-node:hover .node-delete-btn {
          opacity: 1;
        }

        .node-delete-btn:hover {
          background-color: #ff4d4f !important;
          color: white !important;
        }
      `}</style>
    </>
  );
};

export default InputNode;